/**
 * Add/Edit Service dialog (DNKADM-0055 U1) — a MUI `Dialog` that creates/updates a
 * `v2_service` entity via `createService` / `updateService` (handoff §5.5).
 *
 * Core fields: **name** (required — unique on `(tenant, name)`), **description**
 * (optional), **imageUrl** (optional — a plain URL, §Non-Goals), the **credit bucket**
 * (1:1; "None — free service" when unset, via the 0052 fail-closed bucket pick-list),
 * the **staff** + **resource** assignment-mode pickers (`NONE|MEMBER_SELECTS|
 * ROUND_ROBIN`, §4.7), and the **taxable** boolean.
 *
 * **Invariant enforced inline (§4.7):** at least one of staff/resource assignment ≠
 * NONE — the save is blocked with a field error when both are NONE. The category /
 * location / staff / resource ASSIGNMENTS themselves (the join rows) are managed on the
 * service detail (U2), not here; this dialog owns only the core record + the two modes.
 *
 * The presentation pair (DNKADM-0267): **variation display** (a two-option select —
 * durations vs named types, the closed DNKAPI-0421 vocabulary) and **featured**
 * (member-app highlight). Both are preserve-on-null on the wire — an unset control
 * is OMITTED from the body, never sent as a blank.
 *
 * The **booking lead time override** (DNKADM-0385, `@/domain/leadTime`): blank inherits
 * the tenant default and the helper line shows the inherited figure (one
 * `GET /v2/admin/tenant-settings` per open, fail-soft); a set value wins, even when
 * shorter. Written through the billing-day sentinel like `LocationDialog`: on edit the
 * key is always sent (`0` = clear/inherit, `n` = set); on create only when filled.
 *
 * A validation `400` / `409` renders INLINE; anything else raises an error toast
 * (`classifyFormError`). Mirrors `ResourceDialog` (DNKADM-0054).
 *
 * **Split by card (DNKADM-0493).** The service page's cards each edit a slice of these
 * fields in a right-side drawer: {@link ServiceFields} renders only the given `sections`
 * (`basics` / `who` / `rules`) with no dialog chrome, and the drawer's one Save calls its
 * handle. The body is built exactly as before — every field, the hidden ones seeded from the
 * loaded record — so a drawer save sends the same PUT the old dialog sent. A drawer save
 * whose body is identical to the loaded record's sends nothing (the drawer may only be
 * saving an assignment beside it). Embedded, a failure of either kind shows inline (the
 * drawer stays open on it) and there is no toast of its own.
 */
import { useCallback, useId, useImperativeHandle, useState, type Ref } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
} from '@mui/material';
import {
  createService,
  updateService,
  type PaymentMode,
  type ServiceInput,
  type V2Service,
  type VariationDisplay,
} from '@/api/services';
import { useCreditBucketPickList } from '@/api/creditBuckets';
import { getTenantSettings } from '@/api/tenantSettings';
import { PickListLoader } from '@/components/PickListLoader';
import { ASSIGNMENT_MODES, type AssignmentMode } from '@/domain/v2Enums';
import { pickModeLabel } from './serviceLabels';
import {
  LEAD_TIME_BEHAVIOUR,
  leadTimeMinutesLabel,
  parseLeadTimeEntry,
  splitLeadTime,
  type LeadTimeUnit,
} from '@/domain/leadTime';
import { LeadTimeInput } from '@/components/LeadTimeInput';
import { useResource } from '@/hooks/useResource';
import type { ShowToast } from '@/components/toastContext';
import { classifyFormError } from '@/api/screenError';
import { v2ErrorCode } from '@/api/v2Errors';
import { CRM_TRACK_LIMIT, CRM_TRACK_LIMIT_MESSAGE } from '@/api/crm';
import { CRM_DISPLAY_NAME } from '@/utils/whitelabel';

export interface ServiceDialogProps {
  /** Whether the dialog is open. */
  open: boolean;
  /** The service being edited, or `null` to add a new one. */
  service: V2Service | null;
  /** Close the dialog without saving. */
  onClose: () => void;
  /** Called after a successful create/update so the caller can refetch the list. */
  onSaved: () => void;
  /** Raise a non-blocking toast (upstream-error). */
  showToast: ShowToast;
}

/** Which card's slice of the form to render (DNKADM-0493). */
export type ServiceFormSection = 'basics' | 'who' | 'rules';

const ALL_SECTIONS: readonly ServiceFormSection[] = ['basics', 'who', 'rules'];

/** The drawer's handle on an embedded form (DNKADM-0493). */
export interface ServiceFormHandle {
  /** Validate and PUT. `true` = saved or nothing changed; `false` = invalid or refused. */
  save: () => Promise<boolean>;
}

export interface ServiceFieldsProps {
  /** The loaded service (embedded mode always edits). */
  service: V2Service;
  /** The slice of fields to render; the rest are sent as loaded. */
  sections: readonly ServiceFormSection[];
  handleRef: Ref<ServiceFormHandle>;
  showToast: ShowToast;
  /** Called after a successful PUT. */
  onSaved?: () => void;
}

interface FieldErrors {
  name?: string;
  assignment?: string;
  maxGuests?: string;
  leadTimeMinutes?: string;
}

/**
 * The lead-time helper line (DNKADM-0385 U2/U3). Blank inherits the tenant default, so
 * the line says what will ACTUALLY apply — the inherited number, read from the tenant
 * settings — and an operator never opens two screens to answer "how much notice does
 * this service need". `tenant` is `undefined` while that read is pending or failed
 * (fail-soft: the override still saves; only the inherited figure is unknown).
 */
function leadTimeHelp(raw: string, unit: LeadTimeUnit, tenant: number | null | undefined): string {
  // Branch on the parsed value, not the raw text: a typed 0 means "inherit" (spec U1's
  // "blank or zero"), so it reads the inherit line, never "overrides".
  const parsed = parseLeadTimeEntry(raw, unit);
  if (parsed.ok && parsed.value !== null) {
    return `Overrides the tenant lead time, even when shorter. ${LEAD_TIME_BEHAVIOUR}`;
  }
  const inherited =
    tenant === undefined
      ? 'Empty inherits the tenant lead time.'
      : tenant === null
        ? 'Empty inherits the tenant lead time — currently none, so members can book right up to the start.'
        : `Empty inherits the tenant lead time — currently ${leadTimeMinutesLabel(tenant)}.`;
  return `${inherited} ${LEAD_TIME_BEHAVIOUR}`;
}

/** Parse a non-negative integer from text, or null when invalid/blank. */
function parseNonNegativeInt(text: string): number | null {
  const trimmed = text.trim();
  if (trimmed === '') return null;
  const n = Number(trimmed);
  return Number.isFinite(n) && Number.isInteger(n) && n >= 0 ? n : null;
}

export function ServiceDialog(props: ServiceDialogProps) {
  // Remount the inner form per open / per edited service so its inputs reseed.
  if (!props.open) return null;
  return <ServiceDialogForm key={props.service?.id ?? 'new'} {...props} />;
}

/**
 * The card-slice form for a service-page drawer (DNKADM-0493): the given `sections` only,
 * no dialog chrome, saved through `handleRef`.
 */
export function ServiceFields({ service, sections, handleRef, showToast, onSaved }: ServiceFieldsProps) {
  return (
    <ServiceDialogForm
      service={service}
      sections={sections}
      embedded={{ handleRef }}
      onClose={() => {}}
      onSaved={onSaved ?? (() => {})}
      showToast={showToast}
    />
  );
}

/** Trim a string and return `undefined` when empty (omit the optional key). */
function optional(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
}

/** Sentinel value for the "None — free service" bucket option. */
const NO_BUCKET = '';

/** The two known payment-capture modes the UI offers (DNKAPI-0271). */
const PAYMENT_MODE_OPTIONS: { value: PaymentMode; label: string }[] = [
  { value: 'charge', label: 'Charge at booking' },
  { value: 'card_on_file', label: 'Card on file, no charge' },
];

/** Always-appended caveat: the mode governs new bookings only. */
const PAYMENT_MODE_CAVEAT =
  'Changing this affects new bookings; existing reservations keep the capture they were created with.';

/** Helper text: the per-option guidance (when known) plus the future-bookings caveat. */
function paymentModeHelp(mode: PaymentMode): string {
  const guidance =
    mode === 'charge'
      ? 'The guest pays for this service when they book.'
      : mode === 'card_on_file'
        ? "The guest’s card is saved at booking with no charge (e.g. swing evaluation). A no-show fee can be charged later."
        : '';
  return guidance === '' ? PAYMENT_MODE_CAVEAT : `${guidance} ${PAYMENT_MODE_CAVEAT}`;
}

function ServiceDialogForm({
  service,
  onClose,
  onSaved,
  showToast,
  sections = ALL_SECTIONS,
  embedded,
}: Omit<ServiceDialogProps, 'open'> & {
  sections?: readonly ServiceFormSection[];
  embedded?: { handleRef: Ref<ServiceFormHandle> };
}) {
  const ids = useId();
  const isEdit = service !== null;
  const show = (section: ServiceFormSection) => sections.includes(section);
  const buckets = useCreditBucketPickList();

  const [name, setName] = useState(service?.name ?? '');
  const [description, setDescription] = useState(service?.description ?? '');
  const [imageUrl, setImageUrl] = useState(service?.imageUrl ?? '');
  const [creditBucketId, setCreditBucketId] = useState<string>(
    service?.creditBucketId ?? NO_BUCKET,
  );
  const [staffAssignment, setStaffAssignment] = useState<AssignmentMode>(
    service?.staffAssignment ?? 'NONE',
  );
  const [resourceAssignment, setResourceAssignment] = useState<AssignmentMode>(
    service?.resourceAssignment ?? 'NONE',
  );
  const [taxable, setTaxable] = useState(service?.taxable ?? false);
  const [requiresConfirmation, setRequiresConfirmation] = useState(
    service?.requiresConfirmation ?? false,
  );
  const [paymentMode, setPaymentMode] = useState<PaymentMode>(
    service?.paymentMode ?? 'charge',
  );
  const [allowGuests, setAllowGuests] = useState(service?.allowGuests ?? false);
  // Held as text (like the variation number fields) so an empty/invalid entry is
  // validated, not silently coerced. Default "0" (= unlimited when guests are allowed).
  const [maxGuests, setMaxGuests] = useState(String(service?.maxGuests ?? 0));
  // Presentation pair (DNKADM-0267), both preserve-on-null on the wire: '' / undefined
  // mean "no selection" and are OMITTED from the body (create → server defaults;
  // update → the stored value is preserved). Only an actual selection is ever sent.
  const [variationDisplay, setVariationDisplay] = useState<VariationDisplay | ''>(
    service?.variationDisplay ?? '',
  );
  const [featured, setFeatured] = useState<boolean | undefined>(service?.featured);
  // Track in CRM (DNKADM-0499) — preserve-on-omit like `featured`: sent only once it holds a
  // real value (loaded from the record, or set by the switch).
  const [crmTracked, setCrmTracked] = useState<boolean | undefined>(service?.crmTracked);
  // Booking lead time override (DNKADM-0385; units DNKADM-0389) — blank when the service
  // inherits; when set, the largest unit that divides the stored minutes evenly. No default
  // number is ever seeded.
  const [leadTimeAmount, setLeadTimeAmount] = useState(
    service?.leadTimeMinutes !== undefined ? String(splitLeadTime(service.leadTimeMinutes).amount) : '',
  );
  const [leadTimeUnit, setLeadTimeUnit] = useState<LeadTimeUnit>(
    service?.leadTimeMinutes !== undefined ? splitLeadTime(service.leadTimeMinutes).unit : 'minutes',
  );
  // The tenant default the blank field inherits — read once per open so the helper
  // line can show the number that will actually apply. Fail-soft: a failed read only
  // hides the figure (`data` stays null → `undefined` below); the override still saves.
  const tenantSettings = useResource(useCallback(() => getTenantSettings(), []), []);
  const tenantLeadTime =
    tenantSettings.status === 'ready' && tenantSettings.data !== null
      ? tenantSettings.data.leadTimeMinutes
      : undefined;

  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [serverError, setServerError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  /** The body the current field values build, and any field errors. Pure over state. */
  function collect(): { errors: FieldErrors; input: ServiceInput } {
    const errors: FieldErrors = {};
    const tName = name.trim();
    if (tName === '') errors.name = 'Enter a name.';
    // §4.7 invariant: at least one of staff/resource assignment ≠ NONE.
    if (staffAssignment === 'NONE' && resourceAssignment === 'NONE') {
      errors.assignment =
        'Set the coach or the bay (or both) to “Members pick” or “Dink picks”. A service must use at least one.';
    }
    // Guest cap: only validated when guests are allowed (mirror the API — integer ≥ 0,
    // where `0` = unlimited is VALID). When guests are off, maxGuests is sent as 0.
    let maxGuestsValue = 0;
    if (allowGuests) {
      const parsed = parseNonNegativeInt(maxGuests);
      if (parsed === null) {
        errors.maxGuests = 'Enter 0 or a positive whole number (0 = unlimited).';
      } else {
        maxGuestsValue = parsed;
      }
    }
    // Lead time override (DNKADM-0385): blank OK (inherit); else a whole number 1–MAX.
    // Shorter than the tenant value is a legitimate override, never an error.
    const leadTimeResult = parseLeadTimeEntry(leadTimeAmount, leadTimeUnit);
    if (!leadTimeResult.ok) errors.leadTimeMinutes = leadTimeResult.message;

    const input: ServiceInput = {
      name: tName,
      staffAssignment,
      resourceAssignment,
      taxable,
      requiresConfirmation,
      paymentMode,
      allowGuests,
      maxGuests: maxGuestsValue,
      creditBucketId: creditBucketId === NO_BUCKET ? null : creditBucketId,
    };
    const d = optional(description);
    if (d !== undefined) input.description = d;
    const img = optional(imageUrl);
    if (img !== undefined) input.imageUrl = img;
    // DNKADM-0267 preserve-on-null: only an actual selection goes out — an unset
    // select/toggle is omitted, never sent as a blank that would overwrite.
    if (variationDisplay !== '') input.variationDisplay = variationDisplay;
    if (featured !== undefined) input.featured = featured;
    if (crmTracked !== undefined) input.crmTracked = crmTracked;
    // Lead time override (DNKADM-0385) — the LocationDialog billing-day rule:
    // edit: always send (0 = clear → inherit, n = set); create: only when filled
    // (no override = omit the key; the server inherits).
    const leadTimeValue = leadTimeResult.ok ? leadTimeResult.value : null;
    if (isEdit) input.leadTimeMinutes = leadTimeValue !== null ? leadTimeValue : 0;
    else if (leadTimeValue !== null) input.leadTimeMinutes = leadTimeValue;
    return { errors, input };
  }

  function validate(): ServiceInput | null {
    const { errors, input } = collect();
    if (errors.name || errors.assignment || errors.maxGuests || errors.leadTimeMinutes) {
      setFieldErrors(errors);
      return null;
    }
    setFieldErrors({});
    return input;
  }

  // DNKADM-0493: the body the loaded record builds, so an embedded save with nothing
  // changed in its slice sends nothing.
  const [baselineBody] = useState(() => JSON.stringify(collect().input));

  async function handleSave(): Promise<boolean> {
    setServerError(null);
    const input = validate();
    if (!input) return false;
    if (embedded && JSON.stringify(input) === baselineBody) return true;
    setSaving(true);
    try {
      if (isEdit && service) await updateService(service.id, input);
      else await createService(input);
      // DNKADM-0320: confirm the save (after the write resolves; one toast per action).
      // Embedded, the drawer raises the one toast for its whole save.
      if (!embedded) showToast(isEdit ? 'Service saved' : 'Service added', 'success');
      onSaved();
      return true;
    } catch (err) {
      // DNKADM-0499: a 4th tracked service reads the fixed limit line, never the code.
      const view =
        v2ErrorCode(err) === CRM_TRACK_LIMIT
          ? { kind: 'inline' as const, message: CRM_TRACK_LIMIT_MESSAGE }
          : classifyFormError(err, 'The service couldn’t be saved. Please try again.');
      // Embedded, the drawer stays open on the failure, so it is said right here.
      if (view.kind === 'inline' || embedded) setServerError(view.message);
      else showToast(view.message, 'error');
      return false;
    } finally {
      setSaving(false);
    }
  }

  useImperativeHandle(embedded?.handleRef, () => ({ save: handleSave }));

  const titleId = `${ids}-title`;
  const bucketLabelId = `${ids}-bucket-label`;
  const staffLabelId = `${ids}-staff-label`;
  const resourceLabelId = `${ids}-resource-label`;
  const paymentLabelId = `${ids}-payment-label`;
  const variationDisplayLabelId = `${ids}-variation-display-label`;

  // The two known modes, plus the loaded value when it's an unknown future enum (so the
  // control shows it as selected rather than blanking — do not coerce to `charge`).
  const paymentModeOptions = PAYMENT_MODE_OPTIONS.some((o) => o.value === paymentMode)
    ? PAYMENT_MODE_OPTIONS
    : [...PAYMENT_MODE_OPTIONS, { value: paymentMode, label: paymentMode }];

  const fields = (
    <Stack spacing={2.5} sx={{ pt: 0.5 }}>
      {show('basics') && (
        <>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={saving}
            size="small"
            required
            fullWidth
            error={Boolean(fieldErrors.name)}
            helperText={fieldErrors.name}
            slotProps={{ htmlInput: { 'aria-label': 'Name' } }}
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={saving}
            size="small"
            fullWidth
            multiline
            minRows={2}
            slotProps={{ htmlInput: { 'aria-label': 'Description' } }}
          />
          <TextField
            label="Photo link"
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
            disabled={saving}
            size="small"
            fullWidth
            helperText="Optional — a link to a photo for this service."
            slotProps={{ htmlInput: { 'aria-label': 'Photo link', inputMode: 'url' } }}
          />
        </>
      )}

      {/* Credit bucket (1:1; null = free) via the 0052 fail-closed pick-list. */}
      {show('rules') && (
        <PickListLoader list={buckets} label="credit buckets" loadingVariant="inline">
          {(options) => (
            <FormControl size="small" fullWidth disabled={saving}>
              <InputLabel id={bucketLabelId}>Members pay with</InputLabel>
              <Select
                labelId={bucketLabelId}
                label="Members pay with"
                value={creditBucketId}
                onChange={(e) => setCreditBucketId(e.target.value)}
                slotProps={{ input: { 'aria-label': 'Members pay with' } }}
              >
                <MenuItem value={NO_BUCKET}>None — free service</MenuItem>
                {options.map((opt) => (
                  <MenuItem key={opt.id} value={opt.id}>
                    {opt.name === '' ? opt.id : opt.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </PickListLoader>
      )}

      {/* Assignment modes (§4.7) — at least one ≠ NONE. */}
      {show('who') && (
        <>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <FormControl size="small" fullWidth disabled={saving} error={Boolean(fieldErrors.assignment)}>
              <InputLabel id={staffLabelId}>Who picks the coach</InputLabel>
              <Select
                labelId={staffLabelId}
                label="Who picks the coach"
                value={staffAssignment}
                onChange={(e) => setStaffAssignment(e.target.value as AssignmentMode)}
                slotProps={{ input: { 'aria-label': 'Who picks the coach' } }}
              >
                {ASSIGNMENT_MODES.map((mode) => (
                  <MenuItem key={mode} value={mode}>
                    {pickModeLabel(mode)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" fullWidth disabled={saving} error={Boolean(fieldErrors.assignment)}>
              <InputLabel id={resourceLabelId}>Who picks the bay</InputLabel>
              <Select
                labelId={resourceLabelId}
                label="Who picks the bay"
                value={resourceAssignment}
                onChange={(e) => setResourceAssignment(e.target.value as AssignmentMode)}
                slotProps={{ input: { 'aria-label': 'Who picks the bay' } }}
              >
                {ASSIGNMENT_MODES.map((mode) => (
                  <MenuItem key={mode} value={mode}>
                    {pickModeLabel(mode)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
          {fieldErrors.assignment && (
            <Alert severity="error" role="alert" sx={{ mt: -1.5 }}>
              {fieldErrors.assignment}
            </Alert>
          )}
        </>
      )}

      {show('rules') && (
        <>
          <Stack spacing={0}>
            <FormControlLabel
              control={
                <Switch
                  checked={requiresConfirmation}
                  onChange={(e) => setRequiresConfirmation(e.target.checked)}
                  disabled={saving}
                  slotProps={{ input: { 'aria-label': 'Staff approval' } }}
                />
              }
              label="Staff approval"
            />
            <FormHelperText>
              Staff must approve each booking before it’s confirmed.
            </FormHelperText>
          </Stack>

          {/* Booking lead time override (DNKADM-0385) — blank inherits the tenant default and
              the helper line shows the inherited figure; a set value wins, even when shorter. */}
          <LeadTimeInput
            label="Booking lead time (override)"
            amount={leadTimeAmount}
            unit={leadTimeUnit}
            onAmountChange={setLeadTimeAmount}
            onUnitChange={setLeadTimeUnit}
            disabled={saving}
            size="small"
            error={Boolean(fieldErrors.leadTimeMinutes)}
            helperText={
              fieldErrors.leadTimeMinutes ?? leadTimeHelp(leadTimeAmount, leadTimeUnit, tenantLeadTime)
            }
            amountAriaLabel="Booking lead time (override)"
            amountWidth={140}
          />

          {/* Payment capture (DNKAPI-0271) — a Select (extensible enum), not a boolean. The
              UI sends only the two known values; a loaded unknown value stays selectable. */}
          <FormControl size="small" fullWidth disabled={saving}>
            <InputLabel id={paymentLabelId}>Payment at booking</InputLabel>
            <Select
              labelId={paymentLabelId}
              label="Payment at booking"
              value={paymentMode}
              onChange={(e) => setPaymentMode(e.target.value as PaymentMode)}
              slotProps={{ input: { 'aria-label': 'Payment at booking' } }}
            >
              {paymentModeOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>{paymentModeHelp(paymentMode)}</FormHelperText>
          </FormControl>

          {/* Guest policy (DNKAPI-0145): a master toggle revealing the per-reservation cap. */}
          <Stack spacing={1}>
            <FormControlLabel
              control={
                <Switch
                  checked={allowGuests}
                  onChange={(e) => setAllowGuests(e.target.checked)}
                  disabled={saving}
                  slotProps={{ input: { 'aria-label': 'Allow guests' } }}
                />
              }
              label="Allow guests (members may bring guests on a reservation)"
            />
            {allowGuests && (
              <TextField
                label="Max guests per reservation"
                type="number"
                value={maxGuests}
                onChange={(e) => setMaxGuests(e.target.value)}
                disabled={saving}
                size="small"
                error={Boolean(fieldErrors.maxGuests)}
                helperText={fieldErrors.maxGuests ?? '0 = unlimited.'}
                sx={{ maxWidth: 280 }}
                slotProps={{
                  htmlInput: {
                    'aria-label': 'Max guests per reservation',
                    min: 0,
                    step: 1,
                    inputMode: 'numeric',
                  },
                }}
              />
            )}
          </Stack>

          <FormControlLabel
            control={
              <Switch
                checked={taxable}
                onChange={(e) => setTaxable(e.target.checked)}
                disabled={saving}
                slotProps={{ input: { 'aria-label': 'Taxable' } }}
              />
            }
            label="Taxable (tax computed at booking from the location’s active rates)"
          />
        </>
      )}

      {show('basics') && (
        <>
          {/* Presentation pair (DNKADM-0267) — display-only; the select can only ever
              produce the closed duration/type vocabulary; unset is omitted (preserve). */}
          <FormControl size="small" fullWidth disabled={saving}>
            <InputLabel id={variationDisplayLabelId}>Show lengths as</InputLabel>
            <Select
              labelId={variationDisplayLabelId}
              label="Show lengths as"
              value={variationDisplay}
              onChange={(e) => setVariationDisplay(e.target.value as VariationDisplay)}
              slotProps={{ input: { 'aria-label': 'Show lengths as' } }}
            >
              <MenuItem value="duration">Show lengths as durations</MenuItem>
              <MenuItem value="type">Show lengths as named types</MenuItem>
            </Select>
            <FormHelperText>
              How this service’s lengths appear when booking. Left unset, durations are used.
            </FormHelperText>
          </FormControl>

          <Stack spacing={0}>
            <FormControlLabel
              control={
                <Switch
                  checked={featured ?? false}
                  onChange={(e) => setFeatured(e.target.checked)}
                  disabled={saving}
                  slotProps={{ input: { 'aria-label': 'Feature this service in the member app' } }}
                />
              }
              label="Feature this service in the member app"
            />
            <FormHelperText>
              Highlights this service to members. Display-only.
            </FormHelperText>
          </Stack>

          {/* Track in CRM (DNKADM-0499) — an existing service only (the limit is per tenant). */}
          {isEdit && (
            <Stack spacing={0}>
              <FormControlLabel
                control={
                  <Switch
                    checked={crmTracked ?? false}
                    onChange={(e) => setCrmTracked(e.target.checked)}
                    disabled={saving}
                    slotProps={{ input: { 'aria-label': `Track in ${CRM_DISPLAY_NAME}` } }}
                  />
                }
                label={`Track in ${CRM_DISPLAY_NAME}`}
              />
              <FormHelperText>
                Dink keeps “Dink Last {name.trim() || 'this service'}” on every contact: the date of
                their most recent visit for it. Up to 3 services.
              </FormHelperText>
            </Stack>
          )}
        </>
      )}

      {serverError && (
        <Alert severity="error" role="alert">
          {serverError}
        </Alert>
      )}
    </Stack>
  );

  // DNKADM-0493: inside a card's drawer — the drawer supplies the title and the one Save.
  if (embedded) return fields;

  return (
    <Dialog open onClose={onClose} aria-labelledby={titleId} maxWidth="sm" fullWidth>
      <DialogTitle id={titleId}>{isEdit ? 'Edit service' : 'Add service'}</DialogTitle>
      <DialogContent dividers>{fields}</DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving} color="inherit">
          Cancel
        </Button>
        <Button variant="contained" onClick={() => void handleSave()} disabled={saving}>
          {saving ? 'Saving…' : isEdit ? 'Save service' : 'Add service'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
